/* @flow */
import _ from 'lodash';

/**
 * Returns a new array containing the contents of the array list, followed by the given element/s.
 * @param {any[]} array The array.
 * @param {any/any} el The value/values to push.
 * @returns {any[]} The new array.
 */
const append = (originalArray: any[], el: any | any[]) => {
  return Array.isArray(el) ? [...originalArray, ...el] : [...originalArray, el];
};

/**
 * Removes an element from the array (returns a new array).
 * Doesn't work with collections (array of objects).
 * @param {any[]} array The array.
 * @param {any} el The value to pull.
 * @returns {any[]} The new array.
 */
const remove = (originalArray: any[], el: any | any[]) => {
  return Array.isArray(el) ? _.remove(originalArray, x => x === el) : _.without(originalArray, el);
};

/**
 * Iterates over elements of the array/collection, deleting the first element predicate returns
 * truthy for and returning a new array.
 * See Lodash.find() for more info on the quey param.
 * @param {any[]} array The collection to inspect.
 * @param {Object} query The function invoked per iteration / Object used for the query.
 * @returns {any[]} The new array (without the found elements).
 */
const removeBy = (originalArray: any[], query: { [string]: any }) => {
  const elementIndex = _.findIndex(originalArray, query);
  const clonedArray = _.cloneDeep(originalArray);
  if (elementIndex === -1) {
    return clonedArray;
  } else {
    _.pullAt(clonedArray, elementIndex);
    return clonedArray;
  }
};

/**
 * Remove an element from an array if it exists in it, otherwise pushes it (aka toggles it).
 * Returns a new array.
 * @param {any[]} array The array to inspect and update.
 * @param {any} el The value to push/pull.
 * @returns {any[]} The new array.
 */
const appendOrRemove = (array: any[], el: any) => {
  return array.indexOf(el) === -1 ? append(array, el) : remove(array, el);
};

/**
 * Iterates over elements of array, and returns a copy of it replacing the first element predicate returns truthy for with replacement.
 * @param {A[]} array The array to inspect and update.
 * @param {(e: A) => boolean} predicate The function invoked per iteration.
 * @param {A} replacement The value to  invoked per iteration.
 */
const replace = <A>(array: A[], predicate: (e: A) => boolean, replacement: A): A[] => {
  const index = _.findIndex(array, predicate);
  return index === -1 ? array : [...array.slice(0, index), replacement, ...array.slice(index + 1)];
};

const toggle = <A>(array: A[], element: A): A[] => {
  const index = array.find(_ => _ === element);
  return index ? array.filter(_ => _ !== element) : [...array, element]; // [...array.slice(0, index), ...array.slice(index + 1)];
};

/**
 * Given a string/non-decimal-number tries to convert it to a number.
 * Returns undefined if it cannot be converted to a number.
 * @param {string|number} x The string/non-decimal-number to convert.
 * @returns {number} The converted number.
 */
const toNumber = (x: string | number): number | void => {
  if (!x) {
    return undefined;
  }
  if (typeof x === 'number') {
    return x;
  }
  const isNumber = (str: string) => /^\d+(\.\d+)?$/.test(str);
  if (typeof x === 'string' && isNumber(x)) {
    return Number(x);
  }
  return undefined;
};

/**
 * Simple syntactic sugar over setTimeout that looks good when using the async/await pattern.
 * @param {ms} number - Time to wait for in milliseconds.
 * @return {Promise<void>} A promise that resolves when the given time has ended.
 */
const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Given an array of functions and optional arguments, calls each function in a try-catch block with those arguments.
 * @param {functions} Array<Function> - The array of functions to be called.
 * @param {param} any - Optional params can be passed to be used when calling functions.
 * @return {{ successes: Array<any>, errors: Array<any> }} An object with the list of call successes and errors.
 */
const tryEach = (functions: Array<() => mixed>, ...args: any[]) => {
  const errors = [];
  const successes = [];
  // eslint-disable-next-line no-unused-vars
  for (const func of functions) {
    try {
      if (typeof func === 'function') {
        const result = func.apply(null, args || []);
        successes.push(result);
        errors.push(null);
      }
    } catch (err) {
      errors.push(err);
      successes.push(null);
    }
  }
  return {
    successes,
    errors,
  };
};

/**
 * Given an array of functions and optional arguments, calls each function in a try-catch block with those arguments
 * until one such call doesn't throw
 * @param {functions} Array<Function> - The array of functions to be called.
 * @param {param} any - Optional params can be passed to be used when calling functions.
 * @return {{ successes: Array<any>, errors: Array<any> }} An object with the list of call successes and errors.
 */
const tryEachUntilSuccess = (functions: Array<() => mixed>, ...args: any[]) => {
  const errors = [];
  const successes = [];
  // eslint-disable-next-line no-unused-vars
  for (const func of functions) {
    try {
      if (typeof func === 'function') {
        const res = func.apply(null, args || []);
        successes.push(res);
        break;
      }
    } catch (err) {
      errors.push(err);
    }
  }
  return {
    successes,
    errors,
  };
};

/**
 * Given an array of functions and optional arguments, calls each function in a try-catch block with those arguments
 * until one such call throws
 * @param {functions} Array<Function> - The array of functions to be called.
 * @param {param} any - Optional params can be passed to be used when calling functions.
 * @return {{ successes: Array<any>, errors: Array<any> }} An object with the list of call successes and errors.
 */
const tryEachUntilFailure = (functions: Array<() => mixed>, ...args: any[]) => {
  const errors = [];
  const successes = [];
  // eslint-disable-next-line no-unused-vars
  for (const func of functions) {
    try {
      if (typeof func === 'function') {
        const res = func.apply(null, args || []);
        successes.push(res);
      }
    } catch (err) {
      errors.push(err);
      break;
    }
  }
  return {
    successes,
    errors,
  };
};

const throttle = (func: any, milliseconds: number) => {
  let inThrottle;
  let result;
  let hasRunOnce = false;
  return function() {
    const args = arguments;
    const context = this;
    if (func && !inThrottle) {
      inThrottle = true;
      result = func.apply(context, args);
      hasRunOnce = true;
      setTimeout(() => {
        inThrottle = false;
      }, milliseconds);
    }
    while (!hasRunOnce) {}
    return result;
  };
};

const throttleAsync = (func: any, milliseconds: number) => {
  let inThrottle;
  let result;
  let hasRunOnce = false;
  return async function() {
    const args = arguments;
    const context = this;
    if (func && !inThrottle) {
      inThrottle = true;
      result = await func.apply(context, args);
      hasRunOnce = true;
      setTimeout(() => {
        inThrottle = false;
      }, milliseconds);
    } else if (!hasRunOnce) {
      await new Promise(resolve => {
        const intervalId = setInterval(() => {
          if (hasRunOnce) {
            clearInterval(intervalId);
            resolve();
          }
        }, 1000);
      });
    }
    return result;
  };
};

export default {
  appendOrRemove,
  toNumber,
  append,
  remove,
  removeBy,
  replace,
  delay,
  toggle,
  tryEach,
  tryEachUntilSuccess,
  tryEachUntilFailure,
  throttle,
  throttleAsync,
};
